import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import emailjs from "@emailjs/browser";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const ConfigSummary = ({ configData, onBack, onFinish }) => {
  const [customerInfo, setCustomerInfo] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    projectName: "",
    installationAddress: "",
    comments: "",
  });

  const [submitStatus, setSubmitStatus] = useState("idle"); // idle, submitting, success, error
  const [errorMessage, setErrorMessage] = useState("");
  const [pdfDownloadUrl, setPdfDownloadUrl] = useState(null);
  const [showDownloadOptions, setShowDownloadOptions] = useState(false);
  const [activeSection, setActiveSection] = useState("overview");
  const [windowWidth, setWindowWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1200
  );

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const isMobile = windowWidth <= 768;
  const isSmallMobile = windowWidth <= 480;
  const isTablet = windowWidth > 768 && windowWidth <= 1024;

  // Generate unique quote ID
  const quoteId = `ELV-${Date.now().toString().slice(-6)}-${Math.floor(
    Math.random() * 1000
  )}`;

  // Current date
  const currentDate = new Date().toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCustomerInfo((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Add function to convert file to base64 (for email attachment)
  const convertToBase64 = (blob) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result.split(",")[1]);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  };

  // Helper functions to convert code values to human-readable labels
  const formatDoorType = (type) => {
    const types = {
      center: "Center Opening",
      "side-left": "Side Opening - Left",
      "side-right": "Side Opening - Right",
      channel: "Channel Gate",
      glass: "Full Glass Door",
      folding: "Folding Door",
    };
    return types[type] || type || "Standard";
  };

  const formatMaterial = (material) => {
    const materials = {
      stainless: "Stainless Steel",
      rosegold: "Rose Gold",
      wood: "Wood Veneer",
      laminate: "Decorative Laminate",
      glass: "Glass Panels",
      granite: "Granite",
      vinyl: "Vinyl",
      rubber: "Rubber",
      ceramic: "Ceramic Tile",
      gold: "Gold",
    };
    return materials[material] || material || "Standard";
  };

  const formatCeilingType = (type) => {
    const types = {
      classic: "Classic Flat",
      contemporary: "Contemporary",
      suspended: "Suspended Panels",
      backlit: "Backlit Panel",
    };
    return types[type] || type || "Standard";
  };

  const formatLightingType = (type) => {
    const types = {
      "led-spots": "LED Spots",
      linear: "Linear",
      ambient: "Ambient",
      dynamic: "Dynamic",
    };
    return types[type] || type || "Standard";
  };

  // Generate PDF with professional formatting
  const generatePDF = () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.width;
    const marginLeft = 15;
    const marginRight = 15;
    const contentWidth = pageWidth - marginLeft - marginRight;

    // Helper functions
    const addPageHeader = (pageNum, totalPages) => {
      doc.setFontSize(8);
      doc.setTextColor(100);
      doc.text(`ISO 9001:2015`, marginLeft, 10);
      doc.text(`${pageNum} of ${totalPages}`, pageWidth - marginRight, 10, {
        align: "right",
      });
    };

    const addPageFooter = () => {
      doc.setFontSize(9);
      doc.setTextColor(100);
      doc.text(
        `342, Vardhman Nagar, Badarwas, Ajmer Road, Jaipur- 302019`,
        pageWidth / 2,
        287,
        { align: "center" }
      );
    };

    // Page 1 - Cover Letter
    addPageHeader(1, 6);

    doc.setFontSize(10);
    doc.text(`Date: ${currentDate}`, marginLeft, 20);
    doc.text(
      `Ref No.: DCEPL/${configData.buildingInfo.buildingType
        .toUpperCase()
        .substring(0, 2)}/${quoteId}`,
      marginLeft,
      25
    );

    doc.setFontSize(10);
    doc.text("To,", marginLeft, 35);
    doc.text(
      `${customerInfo.name.toUpperCase()}` +
        (customerInfo.phone ? ` (${customerInfo.phone})` : ""),
      marginLeft,
      40
    );
    doc.text(
      `${configData.buildingInfo.buildingType.toUpperCase()}`,
      marginLeft,
      45
    );
    doc.text(
      `${customerInfo.installationAddress || "Address not provided"}`,
      marginLeft,
      50
    );

    doc.setFontSize(11);
    doc.setFont("helvetica", "bold");
    doc.text(
      `Subject: Supply of ${configData.dimensions.capacity} Elevator`,
      marginLeft,
      60
    );

    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.text("Dear Sir/Madam,", marginLeft, 70);

    const letterText = [
      "We thank you for the opportunity to provide a proposal for the supply of a lift at your prestigious Building.",
      "",
      "We are pleased to inform you that DeeCee is one of the best-organized teams of elevator professionals and engineers. We are offering the lift with the latest features and options at the most economical and affordable price range.",
      "",
      "The focus on professional service and reliability are the key attributes that we would like to establish with this long business relationship.",
      "",
      "Please find enclosed the following:",
      "• Technical Specifications",
      "• Commercial Terms & Conditions",
      "",
      "We are sure that you will find this proposal acceptable.",
      "",
      "If you need any further information, please contact us.",
    ];

    let yPos = 75;
    letterText.forEach((line) => {
      if (line === "") {
        yPos += 5;
      } else {
        const splitText = doc.splitTextToSize(line, contentWidth);
        doc.text(splitText, marginLeft, yPos);
        yPos += splitText.length * 5;
      }
    });

    doc.text("Yours Sincerely,", marginLeft, 180);
    doc.text("For DEE CEE ELEVATORS (P) LTD", marginLeft, 190);
    doc.text("VIVEK AGARWAL", marginLeft, 200);
    doc.text("Sales Team", marginLeft, 205);
    doc.text("Mobile: +91 9837221980", marginLeft, 210);
    doc.text("Email: sales@deecee-elevators.com", marginLeft, 215);
    doc.text("Visit us: deeceeelevators.com", marginLeft, 220);

    addPageFooter();

    // Page 2 - Technical Specifications
    doc.addPage();
    addPageHeader(2, 6);

    doc.setFontSize(12);
    doc.setFont("helvetica", "bold");
    doc.text("Technical Specifications", pageWidth / 2, 20, {
      align: "center",
    });

    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.text(
      `Site Address: ${
        customerInfo.installationAddress || "Address not provided"
      }`,
      marginLeft,
      30
    );

    // Create table for tech specs
    const techSpecsData = [
      [
        "Type",
        `${configData.buildingInfo.liftType.toUpperCase()} ${configData.buildingInfo.buildingType.toUpperCase()}`,
      ],
      ["No. of Lift", "01"],
      ["Capacity & Load", configData.dimensions.capacity],
      ["Speed", configData.dimensions.speed],
      ["Travel / Rise", `Approx. ${configData.buildingInfo.floors * 3.5} Mtr.`],
      [
        "Number of Stops/Openings",
        `G+${configData.buildingInfo.floors - 1} ${
          configData.buildingInfo.floors
        }/${
          configData.buildingInfo.floors
        } Opening (All openings on same side)`,
      ],
      ["Drive & Control", "Microprocessor Control Panel with V3F Drive"],
      ["Operation", "Simplex down collective"],
      [
        "Machine",
        `SHARP Gearless traction machine placed directly above hoist way ${
          configData.buildingInfo.liftType === "mrl"
            ? "in shaft"
            : "in machine room"
        }`,
      ],
      [
        "Required Shaft size",
        `Hoist Way Size: ${configData.dimensions.width} wide x ${configData.dimensions.depth} depth.\nPit Depth: 1500 mm  Over Head: 4800 mm (Top Slab)`,
      ],
      [
        "Car Size",
        `${configData.dimensions.width} wide x${configData.dimensions.depth} deep x${configData.dimensions.height} High approx. (As per GAD)`,
      ],
      [
        "Car Design",
        `Car Panels: ${formatMaterial(
          configData.interiorDesign.wallFinish
        )}\n Blower\nFlooring: ${formatMaterial(
          configData.interiorDesign.floorType
        )}`,
      ],
      ["Clear Entrance size", "800 mm wide X 2000 mm high"],
      [
        "Type of Car door",
        `Automatic ${formatDoorType(
          configData.doorConfig.doorType
        )} Door with Frame ${formatMaterial(configData.doorConfig.doorFinish)}`,
      ],
      [
        "Finish of Landing Doors",
        `Automatic ${formatDoorType(
          configData.doorConfig.doorType
        )} Doors with Frames ${formatMaterial(
          configData.doorConfig.doorFinish
        )}`,
      ],
    ];

    // Add features as bullet points
    // Find the generatePDF function and update the features section:

    // Add features as bullet points
    let featuresText = [
      "• Safety Governor",
      "• Emergency stop switch",
      "• Manual Hand winding Rescue Arrangement",
      "• Door safety Lock with Electrical Interlock",
      "• Pit safety switch",
      "• Digital Lift position Indicators",
      "• Call Registration light in car and all buttons",
      "• Battery Operated Emergency Alarm / Light with charger",
      "• V3F Drive",
    ];

    if (isFeatureEnabled(configData.safety, "emergencyPower"))
      featuresText.push("• Emergency Power Operation");
    if (isFeatureEnabled(configData.safety, "fireAlarm", ["fireService"]))
      featuresText.push("• Fire Alarm Integration");
    if (isFeatureEnabled(configData.safety, "securityCamera", ["camera"]))
      featuresText.push("• Security Camera");
    if (isFeatureEnabled(configData.safety, "emergencyCall", ["intercom"]))
      featuresText.push("• Emergency Call System");
    if (isFeatureEnabled(configData.safety, "overload", ["overloadDetection"]))
      featuresText.push("• Overload Detection System");
    if (
      isFeatureEnabled(configData.accessibility, "braille", ["brailleButtons"])
    )
      featuresText.push("• Braille Button Markings");
    if (
      isFeatureEnabled(configData.accessibility, "voiceAnnouncements", [
        "voiceAnnouncement",
      ])
    )
      featuresText.push("• Voice Announcement System");
    if (
      isFeatureEnabled(configData.accessibility, "visibilityMirror", ["mirror"])
    )
      featuresText.push("• Visibility Mirror");
    if (isFeatureEnabled(configData.accessibility, "loweredButtons"))
      featuresText.push("• Lowered Control Buttons");
    if (isFeatureEnabled(configData.accessibility, "handrails"))
      featuresText.push("• ADA Compliant Handrails");

    techSpecsData.push(["Features", featuresText.join("\n")]);
    techSpecsData.push([
      "Power Required",
      "Main Supply: 415 volts, 50 Hz, Three Phase with double earthing.\nLighting Supply: 230 volts, 50Hz, Single Phase",
    ]);

    autoTable(doc, {
      startY: 35,
      head: [],
      body: techSpecsData,
      theme: "plain",
      styles: { cellPadding: 2, fontSize: 9 },
      columnStyles: { 0: { cellWidth: 50, fontStyle: "bold" } },
      margin: { left: marginLeft, right: marginRight },
    });

    addPageFooter();

    // Page 3 - Price and Terms
    doc.addPage();
    addPageHeader(3, 6);

    doc.setFontSize(12);
    doc.setFont("helvetica", "bold");
    doc.text("Price", pageWidth / 2, 20, { align: "center" });

    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.text(
      "Based on your configuration, our team will contact you with a detailed price estimate.",
      marginLeft,
      30
    );
    doc.text(
      "This will include all costs related to the lift system and installation.",
      marginLeft,
      35
    );
    doc.text("IGST @ 18% Extra, F.O.R from JAIPUR Inclusive.", marginLeft, 45);
    doc.text(
      "AMC options will be provided with the detailed quote.",
      marginLeft,
      50
    );

    doc.setFontSize(12);
    doc.setFont("helvetica", "bold");
    doc.text("Your Scope of Work", marginLeft, 70);

    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");

    const clientScope = [
      "• Lift hoist way and machine room of adequate size and strength complete with all civil and steel structural work.",
      "• Single and Three-phase power supply with suitable double earthing, Electrical protective devices ELCB/s and MCB/s.",
      "• Lighting in Lift Shaft and Machine room. Exhaust fan in machine area to meet legal code requirements.",
      "• Power supply and double earthing to be provided up to Lift control panel.",
      "• Adequate lockable weather proof suitable storage space in the proximity of the Lift hoist way.",
      "• Supply and fixing of scaffolding for Installation.",
      "• Whitewash in shaft and machine room.",
      "• 3-phase power connection in machine room with 4-pole MCB switch.",
      "• Double earthing in machine room (8MM copper).",
      "• Minor civil work (machine/buffers/doors frame).",
      "• Electrical line & switches in shaft (single phase).",
      "• One storeroom with lockup key.",
    ];

    let yPos2 = 80;
    clientScope.forEach((line) => {
      const splitText = doc.splitTextToSize(line, contentWidth);
      doc.text(splitText, marginLeft, yPos2);
      yPos2 += splitText.length * 5;
    });

    doc.setFontSize(12);
    doc.setFont("helvetica", "bold");
    doc.text("Commercial Terms & Conditions", marginLeft, 150);

    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");

    const termsData = [
      [
        "Payment Terms",
        "40% + Taxes = Advance along with order.\n50% + Taxes = on Advice that material is ready for dispatch against Performa invoice.\n10% + Taxes = on completion of work before handing over against Performa invoice.",
      ],
      ["Taxes", "Quoted prices are excluding all Taxes."],
      ["Packing, Forwarding", "Inclusive."],
      [
        "Road Permit",
        "FORM- E-WAY road permits for delivery of material on site need to be provided by you.",
      ],
      [
        "Validity",
        "This proposal will remain valid for your acceptance for 30 days from the date of the proposal.",
      ],
      [
        "Delivery & Completion",
        "12-15 weeks from the date of receipt of order, advance payment, layout approval and/or settlement of all technical details, whichever is later.",
      ],
      [
        "Warranty & Free Maintenance",
        "We offer a warranty for 12 months & free maintenance for a period of 12 months running concurrently.",
      ],
    ];

    autoTable(doc, {
      startY: 155,
      head: [],
      body: termsData,
      theme: "plain",
      styles: { cellPadding: 2, fontSize: 9 },
      columnStyles: { 0: { cellWidth: 50, fontStyle: "bold" } },
      margin: { left: marginLeft, right: marginRight },
    });

    addPageFooter();

    // Page 4-5 - Terms and Conditions
    doc.addPage();
    addPageHeader(4, 6);

    doc.setFontSize(12);
    doc.setFont("helvetica", "bold");
    doc.text("Terms and Conditions", pageWidth / 2, 20, { align: "center" });

    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");

    const termsAndConditions = [
      "1. We shall supply, install & commission the Lift/s as per the technical specifications mentioned in our proposal. If any drawings, catalogues, or illustrations are furnished with our proposal, they are general in nature and not specific to your job/building. The layout drawing/s submitted later after the award of contract would contain all the specifications/illustrations specific to your job/building. These layout drawings are submitted to you for your checking and approval. If we do not receive any communications from your side within two weeks, we will take these drawings as approved and start the work as per these drawings. Any change in drawings/specifications or dimensions after the approval of drawings would be carried out at extra cost.",

      "2. This proposal together with terms & conditions shall constitute the contract between us and all prior proposals, quotations, agreements and understanding not incorporated herein are superseded. This contract would be effective only after being signed by our authorized person & credit for advance payment in DEE CEE ELEVATORS (P) LTD Bank account.",

      "3. The agreed contract price shall be valid for a period of 6 months from the date of the reward of the contract. If the contract period were extended beyond 6 months, then a mutually agreeable price increase would be payable extra by you.",

      "4. You would provide proper & uninterrupted power supply and double earthing as required on time. If the power supply is not available by the required date, you should make payments, including the final payments.",

      "5. Any packing cases, left over material, Tools & tackle, Instruments brought to site (including any old part or material replaced during the warranty and free maintenance period) are and shall remain our property.",

      "6. Delay resulting from any cause including but not limited to embargoes, acts of Government, strike, lockout, fire, accident, flood, earthquake, war, delay in supplies of any material due to any or all the above reasons shall extend the time of completion of work. This contract also does not include any liability for loss and consequential damages under any circumstances.",

      "7. The work would be performed during our normal working days and hours.",

      "8. If the materials supplied by us, whether installed or not, are required to be reconditioned/replaced later due to any deterioration/damages on account of non availability of power supply or incomplete work by you or force majeure conditions, the additional cost would be payable extra by you.",

      "9. You will provide us with, in 2 weeks from the date of the award, all required data for the performance of this contract.",

      "10. All disputes & differences of opinion shall be resolved through direct and mutual discussion/s. This contract is deemed to be concluded at JAIPUR and only courts in this place shall have jurisdiction in the event of any dispute whatsoever.",
    ];

    let yPos3 = 30;
    termsAndConditions.forEach((term, index) => {
      // Check if we need to add a page break
      if (yPos3 > 260) {
        addPageFooter();
        doc.addPage();
        addPageHeader(5, 6);
        yPos3 = 30;
      }

      const splitText = doc.splitTextToSize(term, contentWidth);
      doc.text(splitText, marginLeft, yPos3);
      yPos3 += splitText.length * 5 + 3;
    });

    // Continue with more terms if needed
    const moreTerms = [
      "11. We shall provide all assistance in getting the Lift/s inspected by a lift inspector. However, you will pay all license fees and expenses as required for the installation or inspection of the Lifts.",

      "12. If during the inspection of the site, we observe that there is a delay in completion of the Lift pit, Lift shaft and machine room or availability of power supply or any other civil or structure work, we may at our option delay final assembly of materials and shipment of material to site. A fresh date of completion would be established at such an event.",

      "13. The title of Lifts/s will be passed to you only after the credit of all the full & final payment in Dee Cee bank account. Until then all the material supplied will be and shall remain our property.",

      "14. We anticipate that this work can be completed in 08-12 weeks per Lift from the date of receipt of order, advance payment, layout approval and/ or settlement of all technical details, whichever is later. The above completion period is subject to your handing over to us for our uninterrupted use, a clear dry elevator pit, lift shaft and machine room as applicable (complete in all respects along with Scaffolding & 3 phase power supply and double earthing) at least 06-10 weeks before the completion date.",

      "15. We warrant the Lift/s supplied to be free from defects in material and working. Unless agreed otherwise the warranty and free maintenance period would start from the date of issue of lift license. (If applicable) or date of completion.",
    ];

    // Check if we need to add a page break
    if (yPos3 > 200) {
      addPageFooter();
      doc.addPage();
      addPageHeader(5, 6);
      yPos3 = 30;
    }

    moreTerms.forEach((term, index) => {
      // Check if we need to add a page break
      if (yPos3 > 260) {
        addPageFooter();
        doc.addPage();
        addPageHeader(5, 6);
        yPos3 = 30;
      }

      const splitText = doc.splitTextToSize(term, contentWidth);
      doc.text(splitText, marginLeft, yPos3);
      yPos3 += splitText.length * 5 + 3;
    });

    addPageFooter();

    // Page 6 - Signature and bank details
    doc.addPage();
    addPageHeader(6, 6);

    doc.setFontSize(12);
    doc.setFont("helvetica", "bold");
    doc.text("Client Scope of Work", pageWidth / 2, 20, { align: "center" });

    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");

    const clientScopeList = [
      "1. WHITEWASH IN SHAFT AND MACHINE ROOM",
      "2. 3PHASE POWER CONNECTION IN MACHINE ROOM WITH 4POLE MCB SWITCH",
      "3. PROVIDE SCAFFOLDING",
      "4. DOUBLE EARTHING IN MACHINE ROOM (8MM COPPER)",
      "5. MINOR CIVIL WORK (MACHINE/BUFFERS/DOORS FRAME)",
      "6. LANDING SWING DOORS AND FRAME (ALL OPENING)",
      "7. WELDING MACHINE (IF REQUIRED)",
      "8. ELECTRICAL LINE & SWITCHES IN SHAFT (SINGLE PHASE)",
      "9. ONE STOREROOM WITH LOCK UP KEY",
      "10. IRON C CHANNELS (3/4 NOS)",
    ];

    let yPos4 = 30;
    clientScopeList.forEach((item) => {
      doc.text(item, marginLeft, yPos4);
      yPos4 += 7;
    });

    doc.text("For DEE CEE ELEVATORS (P) LTD", marginLeft, 120);
    doc.text("Accepted by Client", pageWidth - marginRight - 50, 120);

    doc.text("Auth. Signatory", marginLeft, 150);
    doc.text(
      "(Signature with Seal and Date)",
      pageWidth - marginRight - 60,
      150
    );

    doc.setFontSize(11);
    doc.setFont("helvetica", "bold");
    doc.text("OUR BANK DETAIL", pageWidth / 2, 180, { align: "center" });

    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.text("DEE CEE ELEVATORS PVT LTD", pageWidth / 2, 190, {
      align: "center",
    });
    doc.text(
      "INDIAN BANK A/C No. 50156288191, IFSC CODE: IDIB000V520",
      pageWidth / 2,
      200,
      { align: "center" }
    );
    doc.text("BRANCH: VIDHYADHAR NAGAR JAIPUR", pageWidth / 2, 210, {
      align: "center",
    });

    addPageFooter();

    return doc;
  };

  // Submit form
const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate form
    if (!customerInfo.name || !customerInfo.email || !customerInfo.phone) {
      setErrorMessage("Please fill in all required fields.");
      return;
    }

    // Set status to submitting
    setSubmitStatus("submitting");

    try {
      // Generate PDF file
      const doc = generatePDF();
      const pdfBlob = doc.output("blob");

      // Create URL for download
      const downloadUrl = URL.createObjectURL(pdfBlob);
      setPdfDownloadUrl(downloadUrl);

      // Convert PDF to Base64 for email attachment
      const pdfBase64 = await convertToBase64(pdfBlob);

      // Format configuration data for the email
      const configSummary = `
        Building Type: ${configData.buildingInfo.buildingType}
        Floors: ${configData.buildingInfo.floors}
        Lift Type: ${configData.buildingInfo.liftType}

        Dimensions: ${configData.dimensions.width} × ${configData.dimensions.depth} × ${
                configData.dimensions.height
              }
        Capacity: ${configData.dimensions.capacity}
        Speed: ${configData.dimensions.speed}

        Door Type: ${formatDoorType(configData.doorConfig.doorType)}
        Door Finish: ${formatMaterial(configData.doorConfig.doorFinish)}

        Ceiling: ${formatCeilingType(configData.interiorDesign.ceilingDesign)}
        Wall Finish: ${formatMaterial(configData.interiorDesign.wallFinish)}
        Floor: ${formatMaterial(configData.interiorDesign.floorType)}
        Handrail: ${configData.interiorDesign.handrailType}
        Lighting: ${formatLightingType(configData.interiorDesign.lighting)}
        Control Panel: ${configData.interiorDesign.controlPanel}

        Safety Features:
        - Emergency Power: ${configData.safety.emergencyPower ? "Yes" : "No"}
        - Fire Service: ${configData.safety.fireService ? "Yes" : "No"}
        - Access Control: ${configData.safety.accessControl ? "Yes" : "No"}
        - Emergency Intercom: ${configData.safety.intercom ? "Yes" : "No"}
        - Security Camera: ${configData.safety.camera ? "Yes" : "No"}
        - Overload Detection: ${configData.safety.overloadDetection ? "Yes" : "No"}

        Accessibility Features:
        - Braille Markings: ${configData.accessibility.brailleButtons ? "Yes" : "No"}
        - Voice Announcements: ${
                configData.accessibility.voiceAnnouncement ? "Yes" : "No"
              }
        - Visibility Mirror: ${configData.accessibility.mirror ? "Yes" : "No"}
        - Lowered Buttons: ${configData.accessibility.loweredButtons ? "Yes" : "No"}
      `;

      console.log("Customer email:", customerInfo.email); // Debug log

      // Send email to customer (confirmation)
      await emailjs.send(
        "service_qgyi6fi",
        "template_anmsmjr",
        {
          to_name: customerInfo.name,
          to_email: customerInfo.email,
          from_name: "Dee Cee Elevators",
          subject: `Elevator Configuration Quote - ${quoteId}`,
          quote_id: quoteId,
          customer_name: customerInfo.name,
          customer_email: customerInfo.email,
          customer_phone: customerInfo.phone,
          company: customerInfo.company || "Not provided",
          project_name: customerInfo.projectName || "Not provided",
          installation_address: customerInfo.installationAddress || "Not provided",
          comments: customerInfo.comments || "None",
          configuration_summary: configSummary,
          date: currentDate,
          pdf_file: pdfBase64,
          pdf_filename: `elevator-quote-${quoteId}.pdf`,
          email_type: "customer_confirmation",
          customer_message: `Dear ${customerInfo.name}, thank you for choosing Dee Cee Elevators. Your elevator configuration has been successfully submitted.`,
          follow_up_message: "We will contact you within 48 hours with a detailed quotation and timeline for your project.",
          company_intro: "Dee Cee Elevators has been serving customers with premium elevator solutions for over a decade.",
          estimated_completion: "12-15 weeks from order confirmation",
          warranty_period: "12 months comprehensive warranty",
          next_steps: "Our technical team will review your configuration and contact you within 48 hours",
          support_email: "info@deeceeelevators.com",
          sales_contact: "+91 9837221980",
        },
        "37S4Byf9tJwfzQ0e1"
      );

      // Send email to company email 1 (internal notification)
      await emailjs.send(
        "service_qgyi6fi",
        "template_anmsmjr",
        {
          to_name: "Sales Team",
          to_email: "info@deeceeelevators.com",
          from_name: "Website Configurator",
          subject: `New Elevator Configuration Lead - ${quoteId}`,
          quote_id: quoteId,
          customer_name: customerInfo.name,
          customer_email: customerInfo.email,
          customer_phone: customerInfo.phone,
          company: customerInfo.company || "Not provided",
          project_name: customerInfo.projectName || "Not provided",
          installation_address: customerInfo.installationAddress || "Not provided",
          comments: customerInfo.comments || "None",
          configuration_summary: configSummary,
          date: currentDate,
          pdf_file: pdfBase64,
          pdf_filename: `elevator-quote-${quoteId}.pdf`,
          email_type: "company_notification",
          priority_level: "Standard",
          lead_source: "Website Configurator",
          assigned_team: "Sales & Technical Team",
          internal_notes: `New lead from ${customerInfo.company || "Individual Customer"} - ${configData.buildingInfo.buildingType} project`,
          follow_up_deadline: "Within 48 hours",
          estimated_project_value: "To be calculated based on configuration",
          next_steps: "Please review attached configuration and contact customer within 48 hours",
          support_email: "info@deeceeelevators.com",
          sales_contact: "+91 9837221980",
        },
        "37S4Byf9tJwfzQ0e1"
      );

      // Send email to company email 2 (backup notification)
      await emailjs.send(
        "service_qgyi6fi",
        "template_anmsmjr",
        {
          to_name: "Sales Team",
          to_email: "info@deeceeelevators.in",
          from_name: "Website Configurator",
          subject: `Backup Notification - New Elevator Lead ${quoteId}`,
          quote_id: quoteId,
          customer_name: customerInfo.name,
          customer_email: customerInfo.email,
          customer_phone: customerInfo.phone,
          company: customerInfo.company || "Not provided",
          project_name: customerInfo.projectName || "Not provided",
          installation_address: customerInfo.installationAddress || "Not provided",
          comments: customerInfo.comments || "None",
          configuration_summary: configSummary,
          date: currentDate,
          pdf_file: pdfBase64,
          pdf_filename: `elevator-quote-${quoteId}.pdf`,
          email_type: "company_notification",
          priority_level: "Standard",
          lead_source: "Website Configurator",
          assigned_team: "Sales & Technical Team",
          internal_notes: `Backup notification - New elevator inquiry from ${customerInfo.name}`,
          follow_up_deadline: "Within 48 hours",
          estimated_project_value: "To be calculated based on configuration",
          next_steps: "Please review attached configuration and contact customer within 48 hours",
          support_email: "info@deeceeelevators.com",
          sales_contact: "+91 9837221980",
        },
        "37S4Byf9tJwfzQ0e1"
      );

      // Set status to success
      setSubmitStatus("success");

      // Show download options after 1 second
      setTimeout(() => {
        setShowDownloadOptions(true);
      }, 1000);

    } catch (error) {
      console.error("Error submitting configuration:", error);
      setErrorMessage(
        `There was an error submitting your configuration: ${error.text || error.message || "Please try again later."}`
      );
      setSubmitStatus("error");
    }
  };

  const getResponsiveStyles = () => {
    return {
      progressContainer: {
        padding: isSmallMobile ? "0 5px" : "0",
      },
      container: {
        padding: isSmallMobile ? "15px" : isMobile ? "15px" : "20px",
      },
      tabsContainer: {
        flexDirection: isSmallMobile ? "row" : "row",
        flexWrap: "wrap",
        gap: isSmallMobile ? "8px" : "12px",
        marginBottom: "25px",
      },
      tab: {
        width: isSmallMobile ? "100%" : "auto",
        padding: isSmallMobile ? "10px 15px" : "12px 18px",
        flexDirection: isMobile && !isSmallMobile ? "row" : "column",
        gap: isMobile && !isSmallMobile ? "8px" : "6px",
      },
      tabIconContainer: {
        width: isSmallMobile ? "36px" : "44px",
        height: isSmallMobile ? "36px" : "44px",
      },
      tabText: {
        fontSize: isSmallMobile ? "12px" : "14px",
      },
      contentContainer: {
        padding: isSmallMobile ? "15px" : isMobile ? "20px" : "30px",
      },
      quoteHeader: {
        flexDirection: isMobile ? "column" : "row",
        gap: isMobile ? "15px" : "0",
      },
      overviewContainer: {
        gridTemplateColumns: isSmallMobile
          ? "1fr"
          : isMobile
          ? "repeat(2, 1fr)"
          : "repeat(auto-fit, minmax(220px, 1fr))",
      },
      dimensionsContainer: {
        gridTemplateColumns: isSmallMobile
          ? "1fr"
          : isMobile
          ? "repeat(2, 1fr)"
          : "repeat(auto-fill, minmax(180px, 1fr))",
      },
      designItemsGrid: {
        gridTemplateColumns: isSmallMobile
          ? "1fr"
          : isMobile
          ? "repeat(2, 1fr)"
          : "repeat(auto-fill, minmax(200px, 1fr))",
      },
      featuresContainer: {
        gridTemplateColumns: isSmallMobile
          ? "1fr"
          : isMobile
          ? "1fr"
          : "repeat(auto-fill, minmax(300px, 1fr))",
      },
      navigationButtons: {
        flexDirection: isSmallMobile ? "column" : "row",
        gap: isSmallMobile ? "15px" : "0",
      },
      formGrid: {
        gridTemplateColumns: isSmallMobile
          ? "1fr"
          : isMobile
          ? "1fr"
          : "repeat(2, 1fr)",
      },
      formButtons: {
        flexDirection: isSmallMobile ? "column" : "row",
        gap: isSmallMobile ? "15px" : "0",
      },
      successMessage: {
        padding: isSmallMobile ? "20px 10px" : "30px 20px",
      },
      downloadSection: {
        flexDirection: isSmallMobile ? "column" : "column",
        width: isSmallMobile ? "100%" : "auto",
      },
      downloadLink: {
        width: isSmallMobile ? "100%" : "auto",
        justifyContent: isSmallMobile ? "center" : "flex-start",
      },
      finishButton: {
        width: isSmallMobile ? "100%" : "auto",
      },
    };
  };

  // Get responsive styles based on current window width
  const responsiveStyles = getResponsiveStyles();

  const getProgressPercentage = () => {
    const sections = [
      "overview",
      "specifications",
      "design",
      "features",
      "contact",
    ];
    const currentIndex = sections.indexOf(activeSection);
    return (currentIndex / (sections.length - 1)) * 100;
  };

  // Update the isFeatureEnabled function to correctly map between array values and object properties
  const isFeatureEnabled = (featureData, featureName, alternateNames = []) => {
    // Handle undefined or null data
    if (!featureData) return false;

    // If it's an array, check if the feature name is included
    if (Array.isArray(featureData)) {
      return (
        featureData.includes(featureName) ||
        alternateNames.some((name) => featureData.includes(name))
      );
    }

    // If it's an object, check if the feature property is true
    // or if any of the alternate name properties are true
    return (
      !!featureData[featureName] ||
      alternateNames.some((name) => !!featureData[name])
    );
  };

  return (
    <div style={{ ...styles.container, ...responsiveStyles.container }}>
      <div style={styles.progressContainer}>
        <div style={styles.progressLabel}>
          <span style={styles.progressStep}>
            Step{" "}
            {activeSection === "overview"
              ? "1"
              : activeSection === "specifications"
              ? "2"
              : activeSection === "design"
              ? "3"
              : activeSection === "features"
              ? "4"
              : "5"}{" "}
            of 5
          </span>
          <span style={styles.progressTitle}>
            {activeSection === "overview"
              ? "Overview"
              : activeSection === "specifications"
              ? "Building Specifications"
              : activeSection === "design"
              ? "Design & Finish"
              : activeSection === "features"
              ? "Features"
              : "Contact Info"}
          </span>
        </div>
        <div style={styles.progressBarContainer}>
          <motion.div
            style={styles.progressBar}
            initial={{ width: 0 }}
            animate={{ width: `${getProgressPercentage()}%` }}
            transition={{ duration: 0.5, ease: "easeInOut" }}
          />
        </div>
      </div>

      {/* Tabs Navigation */}
      <div
        style={{ ...styles.tabsContainer, ...responsiveStyles.tabsContainer }}
      >
        {/* Overview Tab */}
        <motion.button
          style={{
            ...styles.tab,
            ...responsiveStyles.tab,
            ...(activeSection === "overview" ? styles.activeTab : {}),
          }}
          onClick={() => setActiveSection("overview")}
          whileHover={{ scale: 1.03, y: -2 }}
          whileTap={{ scale: 0.97, y: 0 }}
          transition={{ type: "spring", stiffness: 400, damping: 15 }}
        >
          <div style={styles.tabIconContainer}>
            <i className="fas fa-th-large" style={styles.tabIcon}></i>
          </div>
          <span style={styles.tabText}>Overview</span>
          {activeSection === "overview" && (
            <div style={styles.tabIndicator}></div>
          )}
        </motion.button>

        {/* Specifictions Tab */}
        <motion.button
          style={{
            ...styles.tab,
            ...responsiveStyles.tab,
            ...(activeSection === "specifictions" ? styles.activeTab : {}),
          }}
          onClick={() => setActiveSection("specifictions")}
          whileHover={{ scale: 1.03, y: -2 }}
          whileTap={{ scale: 0.97, y: 0 }}
          transition={{ type: "spring", stiffness: 400, damping: 15 }}
        >
          <div style={styles.tabIconContainer}>
            <i className="fas fa-ruler-combined" style={styles.tabIcon}></i>
          </div>
          <span style={styles.tabText}>specifictions</span>
          {activeSection === "specifictions" && (
            <div style={styles.tabIndicator}></div>
          )}
        </motion.button>

        {/* Design Tab */}
        <motion.button
          style={{
            ...styles.tab,
            ...responsiveStyles.tab,
            ...(activeSection === "design" ? styles.activeTab : {}),
          }}
          onClick={() => setActiveSection("design")}
          whileHover={{ scale: 1.03, y: -2 }}
          whileTap={{ scale: 0.97, y: 0 }}
          transition={{ type: "spring", stiffness: 400, damping: 15 }}
        >
          <div style={styles.tabIconContainer}>
            <i className="fas fa-paint-roller" style={styles.tabIcon}></i>
          </div>
          <span style={styles.tabText}>Design</span>
          {activeSection === "design" && (
            <div style={styles.tabIndicator}></div>
          )}
        </motion.button>

        {/* Features Tab */}
        <motion.button
          style={{
            ...styles.tab,
            ...responsiveStyles.tab,
            ...(activeSection === "features" ? styles.activeTab : {}),
          }}
          onClick={() => setActiveSection("features")}
          whileHover={{ scale: 1.03, y: -2 }}
          whileTap={{ scale: 0.97, y: 0 }}
          transition={{ type: "spring", stiffness: 400, damping: 15 }}
        >
          <div style={styles.tabIconContainer}>
            <i className="fas fa-shield-alt" style={styles.tabIcon}></i>
          </div>
          <span style={styles.tabText}>Features</span>
          {activeSection === "features" && (
            <div style={styles.tabIndicator}></div>
          )}
        </motion.button>

        {/* Contact Tab */}
        <motion.button
          style={{
            ...styles.tab,
            ...responsiveStyles.tab,
            ...(activeSection === "contact" ? styles.activeTab : {}),
          }}
          onClick={() => setActiveSection("contact")}
          whileHover={{ scale: 1.03, y: -2 }}
          whileTap={{ scale: 0.97, y: 0 }}
          transition={{ type: "spring", stiffness: 400, damping: 15 }}
        >
          <div style={styles.tabIconContainer}>
            <i className="fas fa-envelope" style={styles.tabIcon}></i>
          </div>
          <span style={styles.tabText}>Contact</span>
          {activeSection === "contact" && (
            <div style={styles.tabIndicator}></div>
          )}
        </motion.button>
      </div>
      {/* Page Content */}
      <div
        style={{
          ...styles.contentContainer,
          ...responsiveStyles.contentContainer,
        }}
      >
        {/* Header with Quote ID and Date */}
        <div style={{ ...styles.quoteHeader, ...responsiveStyles.quoteHeader }}>
          <div>
            <h3 style={styles.quoteTitle}>Elevator Configuration</h3>
            <p style={styles.quoteId}>Quote ID: {quoteId}</p>
          </div>
          <div style={styles.dateBox}>
            <i className="far fa-calendar-alt" style={styles.dateIcon}></i>
            <span>{currentDate}</span>
          </div>
        </div>

        {/* Overview Section */}
        {activeSection === "overview" && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
          >
            <div
              style={{
                ...styles.overviewContainer,
                ...responsiveStyles.overviewContainer,
              }}
            >
              {/* Building info card */}
              <div style={styles.overviewCard}>
                <div style={styles.overviewIconContainer}>
                  <i
                    className="fas fa-building"
                    style={styles.overviewIcon}
                  ></i>
                </div>
                <h4 style={styles.overviewTitle}>Building</h4>
                <p style={styles.overviewValue}>
                  {configData.buildingInfo.buildingType
                    .charAt(0)
                    .toUpperCase() +
                    configData.buildingInfo.buildingType.slice(1)}
                </p>
                <p style={styles.overviewDetail}>
                  {configData.buildingInfo.floors} floors ·{" "}
                  {configData.buildingInfo.liftType}
                </p>
                <button
                  style={styles.overviewButton}
                  onClick={() => setActiveSection("specifictions")}
                >
                  View Details
                </button>
              </div>

              {/* Dimensions card */}
              <div style={styles.overviewCard}>
                <div style={styles.overviewIconContainer}>
                  <i
                    className="fas fa-ruler-combined"
                    style={styles.overviewIcon}
                  ></i>
                </div>
                <h4 style={styles.overviewTitle}>Dimensions</h4>
                <p style={styles.overviewValue}>
                  {configData.dimensions.width} × {configData.dimensions.depth}
                </p>
                <p style={styles.overviewDetail}>
                  Capacity: {configData.dimensions.capacity}
                </p>
                <button
                  style={styles.overviewButton}
                  onClick={() => setActiveSection("specifictions")}
                >
                  View Details
                </button>
              </div>

              {/* Door Type card */}
              <div style={styles.overviewCard}>
                <div style={styles.overviewIconContainer}>
                  <i
                    className="fas fa-door-open"
                    style={styles.overviewIcon}
                  ></i>
                </div>
                <h4 style={styles.overviewTitle}>Door Type</h4>
                <p style={styles.overviewValue}>
                  {formatDoorType(configData.doorConfig.doorType)}
                </p>
                <p style={styles.overviewDetail}>
                  Finish: {formatMaterial(configData.doorConfig.doorFinish)}
                </p>
                <button
                  style={styles.overviewButton}
                  onClick={() => setActiveSection("design")}
                >
                  View Details
                </button>
              </div>

              {/* Interior card */}
              <div style={styles.overviewCard}>
                <div style={styles.overviewIconContainer}>
                  <i
                    className="fas fa-paint-brush"
                    style={styles.overviewIcon}
                  ></i>
                </div>
                <h4 style={styles.overviewTitle}>Interior</h4>
                <p style={styles.overviewValue}>
                  {formatMaterial(configData.interiorDesign.wallFinish)} Walls
                </p>
                <p style={styles.overviewDetail}>
                  {formatMaterial(configData.interiorDesign.floorType)} Floor
                </p>
                <button
                  style={styles.overviewButton}
                  onClick={() => setActiveSection("design")}
                >
                  View Details
                </button>
              </div>

              {/* Safety Features card */}
              <div style={styles.overviewCard}>
                <div style={styles.overviewIconContainer}>
                  <i
                    className="fas fa-shield-alt"
                    style={styles.overviewIcon}
                  ></i>
                </div>
                <h4 style={styles.overviewTitle}>Safety</h4>
                <p style={styles.overviewValue}>
                  {
                    Object.entries(configData.safety || {}).filter(
                      ([_, value]) => value === true
                    ).length
                  }{" "}
                  Features
                </p>
                <p style={styles.overviewDetail}>
                  {isFeatureEnabled(configData.safety, "emergencyPower") &&
                    "Emergency Power · "}
                  {isFeatureEnabled(configData.safety, "fireAlarm", [
                    "fireService",
                  ]) && "Fire Service"}
                </p>
                <button
                  style={styles.overviewButton}
                  onClick={() => setActiveSection("features")}
                >
                  View Details
                </button>
              </div>

              {/* Accessibility Features card */}
              <div style={styles.overviewCard}>
                <div style={styles.overviewIconContainer}>
                  <i
                    className="fas fa-universal-access"
                    style={styles.overviewIcon}
                  ></i>
                </div>
                <h4 style={styles.overviewTitle}>Accessibility</h4>
                <p style={styles.overviewValue}>
                  {
                    Object.entries(configData.accessibility || {}).filter(
                      ([_, value]) => value === true
                    ).length
                  }{" "}
                  Features
                </p>
                <p style={styles.overviewDetail}>
                  {isFeatureEnabled(configData.accessibility, "braille", [
                    "brailleButtons",
                  ]) && "Braille · "}
                  {isFeatureEnabled(
                    configData.accessibility,
                    "voiceAnnouncements",
                    ["voiceAnnouncement"]
                  ) && "Voice Announcements"}
                </p>
                <button
                  style={styles.overviewButton}
                  onClick={() => setActiveSection("features")}
                >
                  View Details
                </button>
              </div>
            </div>

            <div style={styles.submitContainer}>
              <p style={styles.submitText}>
                Your configuration is ready. Please provide your contact details
                to submit your inquiry.
              </p>
              <button
                style={styles.submitButton}
                onClick={() => setActiveSection("contact")}
              >
                Proceed to Contact Form
                <i className="fas fa-arrow-right" style={styles.buttonIcon}></i>
              </button>
            </div>
          </motion.div>
        )}

        {/* Specifictions Section */}
        {activeSection === "specifictions" && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
            style={styles.sectionContent}
          >
            <div style={styles.sectionHeader}>
              <i className="fas fa-building" style={styles.sectionIcon}></i>
              <h3>Building & Technical Specifications</h3>
            </div>

            {/* Building Information Section */}
            <div style={styles.specSectionDivider}>
              <span style={styles.specSectionTitle}>
                <i
                  className="fas fa-info-circle"
                  style={styles.specSectionIcon}
                ></i>
                Building Information
              </span>
            </div>

            <div
              style={{
                ...styles.dimensionsContainer,
                ...responsiveStyles.dimensionsContainer,
              }}
            >
              <div style={styles.dimensionCard}>
                <div style={styles.dimensionIconWrap}>
                  <i
                    className="fas fa-building"
                    style={styles.dimensionIcon}
                  ></i>
                </div>
                <div style={styles.dimensionLabel}>Building Type</div>
                <div style={styles.dimensionValue}>
                  {configData.buildingInfo.buildingType
                    .charAt(0)
                    .toUpperCase() +
                    configData.buildingInfo.buildingType.slice(1)}
                </div>
              </div>

              <div style={styles.dimensionCard}>
                <div style={styles.dimensionIconWrap}>
                  <i
                    className="fas fa-layer-group"
                    style={styles.dimensionIcon}
                  ></i>
                </div>
                <div style={styles.dimensionLabel}>Floors</div>
                <div style={styles.dimensionValue}>
                  {configData.buildingInfo.floors}
                </div>
              </div>

              <div style={styles.dimensionCard}>
                <div style={styles.dimensionIconWrap}>
                  <i className="fas fa-cog" style={styles.dimensionIcon}></i>
                </div>
                <div style={styles.dimensionLabel}>Lift Type</div>
                <div style={styles.dimensionValue}>
                  {configData.buildingInfo.liftType.toUpperCase()}
                </div>
              </div>

              <div style={styles.dimensionCard}>
                <div style={styles.dimensionIconWrap}>
                  <i
                    className="fas fa-arrows-alt"
                    style={styles.dimensionIcon}
                  ></i>
                </div>
                <div style={styles.dimensionLabel}>Travel Height</div>
                <div style={styles.dimensionValue}>
                  ~{(configData.buildingInfo.floors * 3.5).toFixed(1)} m
                </div>
              </div>
            </div>

            {/* Elevator Dimensions Section */}
            <div style={styles.specSectionDivider}>
              <span style={styles.specSectionTitle}>
                <i
                  className="fas fa-ruler-combined"
                  style={styles.specSectionIcon}
                ></i>
                Elevator Dimensions
              </span>
            </div>

            <div
              style={{
                ...styles.dimensionsContainer,
                ...responsiveStyles.dimensionsContainer,
              }}
            >
              <div style={styles.dimensionCard}>
                <div style={styles.dimensionIconWrap}>
                  <i
                    className="fas fa-arrows-alt-h"
                    style={styles.dimensionIcon}
                  ></i>
                </div>
                <div style={styles.dimensionLabel}>Car Width</div>
                <div style={styles.dimensionValue}>
                  {configData.dimensions.width}
                </div>
              </div>

              <div style={styles.dimensionCard}>
                <div style={styles.dimensionIconWrap}>
                  <i
                    className="fas fa-arrows-alt-v"
                    style={styles.dimensionIcon}
                  ></i>
                </div>
                <div style={styles.dimensionLabel}>Car Depth</div>
                <div style={styles.dimensionValue}>
                  {configData.dimensions.depth}
                </div>
              </div>

              <div style={styles.dimensionCard}>
                <div style={styles.dimensionIconWrap}>
                  <i
                    className="fas fa-ruler-vertical"
                    style={styles.dimensionIcon}
                  ></i>
                </div>
                <div style={styles.dimensionLabel}>Car Height</div>
                <div style={styles.dimensionValue}>
                  {configData.dimensions.height}
                </div>
              </div>

              <div style={styles.dimensionCard}>
                <div style={styles.dimensionIconWrap}>
                  <i
                    className="fas fa-door-open"
                    style={styles.dimensionIcon}
                  ></i>
                </div>
                <div style={styles.dimensionLabel}>Door Opening</div>
                <div style={styles.dimensionValue}>800 × 2000 mm</div>
              </div>
            </div>

            {/* Installation Requirements */}
            <div style={styles.specSectionDivider}>
              <span style={styles.specSectionTitle}>
                <i className="fas fa-wrench" style={styles.specSectionIcon}></i>
                Installation Requirements
              </span>
            </div>

            <div
              style={{
                ...styles.dimensionsContainer,
                ...responsiveStyles.dimensionsContainer,
              }}
            >
              <div style={styles.dimensionCard}>
                <div style={styles.dimensionIconWrap}>
                  <i
                    className="fas fa-expand-alt"
                    style={styles.dimensionIcon}
                  ></i>
                </div>
                <div style={styles.dimensionLabel}>Shaft Size</div>
                <div style={styles.dimensionValue}>
                  {configData.dimensions.width} × {configData.dimensions.depth}
                </div>
              </div>

              <div style={styles.dimensionCard}>
                <div style={styles.dimensionIconWrap}>
                  <i className="fas fa-weight" style={styles.dimensionIcon}></i>
                </div>
                <div style={styles.dimensionLabel}>Capacity</div>
                <div style={styles.dimensionValue}>
                  {configData.dimensions.capacity}
                </div>
              </div>

              <div style={styles.dimensionCard}>
                <div style={styles.dimensionIconWrap}>
                  <i
                    className="fas fa-tachometer-alt"
                    style={styles.dimensionIcon}
                  ></i>
                </div>
                <div style={styles.dimensionLabel}>Speed</div>
                <div style={styles.dimensionValue}>
                  {configData.dimensions.speed}
                </div>
              </div>
            </div>

            <div style={styles.navigationButtons}>
              <button
                style={styles.backButton}
                onClick={() => setActiveSection("overview")}
              >
                <i className="fas fa-arrow-left"></i> Back to Overview
              </button>
              <button
                style={styles.nextButton}
                onClick={() => setActiveSection("design")}
              >
                Design Details <i className="fas fa-arrow-right"></i>
              </button>
            </div>
          </motion.div>
        )}

        {/* Design Section */}
        {activeSection === "design" && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
            style={styles.sectionContent}
          >
            <div style={styles.sectionHeader}>
              <i className="fas fa-paint-roller" style={styles.sectionIcon}></i>
              <h3>Design & Finish</h3>
            </div>

            <div style={styles.designGrid}>
              <div style={styles.designCategory}>
                <h4 style={styles.designCategoryTitle}>Door Design</h4>
                <div
                  style={{
                    ...styles.designItemsGrid,
                    ...responsiveStyles.designItemsGrid,
                  }}
                >
                  <div style={styles.designItem}>
                    <div style={styles.designItemLabel}>Door Type</div>
                    <div style={styles.designItemValue}>
                      {formatDoorType(configData.doorConfig.doorType)}
                    </div>
                  </div>
                  <div style={styles.designItem}>
                    <div style={styles.designItemLabel}>Door Finish</div>
                    <div style={styles.designItemValue}>
                      {formatMaterial(configData.doorConfig.doorFinish)}
                    </div>
                  </div>
                </div>
              </div>

              <div style={styles.designCategory}>
                <h4 style={styles.designCategoryTitle}>Interior Design</h4>
                <div style={styles.designItemsGrid}>
                  <div style={styles.designItem}>
                    <div style={styles.designItemLabel}>Wall Finish</div>
                    <div style={styles.designItemValue}>
                      {formatMaterial(configData.interiorDesign.wallFinish)}
                    </div>
                  </div>
                  <div style={styles.designItem}>
                    <div style={styles.designItemLabel}>Wall Layout</div>
                    <div style={styles.designItemValue}>
                      {configData.interiorDesign.wallLayout || "Standard"}
                    </div>
                  </div>
                  <div style={styles.designItem}>
                    <div style={styles.designItemLabel}>Floor Type</div>
                    <div style={styles.designItemValue}>
                      {formatMaterial(configData.interiorDesign.floorType)}
                    </div>
                  </div>
                  <div style={styles.designItem}>
                    <div style={styles.designItemLabel}>Floor Pattern</div>
                    <div style={styles.designItemValue}>
                      {configData.interiorDesign.floorPattern || "Standard"}
                    </div>
                  </div>
                  <div style={styles.designItem}>
                    <div style={styles.designItemLabel}>Handrail Type</div>
                    <div style={styles.designItemValue}>
                      {configData.interiorDesign.handrailType || "Standard"}
                    </div>
                  </div>
                  <div style={styles.designItem}>
                    <div style={styles.designItemLabel}>Handrail Finish</div>
                    <div style={styles.designItemValue}>
                      {formatMaterial(configData.interiorDesign.handrailFinish)}
                    </div>
                  </div>
                </div>
              </div>

              {/* Add a new Additional Features category */}
              <div style={styles.designCategory}>
                <h4 style={styles.designCategoryTitle}>Additional Features</h4>
                <div style={styles.designItemsGrid}>
                  <div style={styles.designItem}>
                    <div style={styles.designItemLabel}>Mirror</div>
                    <div style={styles.designItemValue}>
                      {configData.interiorDesign.mirror
                        ? "Included"
                        : "Not Included"}
                    </div>
                  </div>
                  <div style={styles.designItem}>
                    <div style={styles.designItemLabel}>Display</div>
                    <div style={styles.designItemValue}>
                      {configData.interiorDesign.display
                        ? "Included"
                        : "Not Included"}
                    </div>
                  </div>
                  <div style={styles.designItem}>
                    <div style={styles.designItemLabel}>Ventilation</div>
                    <div style={styles.designItemValue}>
                      {configData.interiorDesign.ventilation
                        ? "Included"
                        : "Not Included"}
                    </div>
                  </div>
                  <div style={styles.designItem}>
                    <div style={styles.designItemLabel}>Kick Plates</div>
                    <div style={styles.designItemValue}>
                      {configData.interiorDesign.kickPlates
                        ? "Included"
                        : "Not Included"}
                    </div>
                  </div>
                </div>
              </div>

              {/* Update the Lighting & Controls section */}
              <div style={styles.designCategory}>
                <h4 style={styles.designCategoryTitle}>Lighting & Controls</h4>
                <div style={styles.designItemsGrid}>
                  <div style={styles.designItem}>
                    <div style={styles.designItemLabel}>Lighting Style</div>
                    <div style={styles.designItemValue}>
                      {formatLightingType(configData.interiorDesign.lighting)}
                    </div>
                  </div>
                  <div style={styles.designItem}>
                    <div style={styles.designItemLabel}>Control Panel</div>
                    <div style={styles.designItemValue}>
                      {configData.interiorDesign.controlPanel}
                    </div>
                  </div>
                  <div style={styles.designItem}>
                    <div style={styles.designItemLabel}>Control Finish</div>
                    <div style={styles.designItemValue}>
                      {formatMaterial(configData.interiorDesign.controlFinish)}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div style={styles.navigationButtons}>
              <button
                style={styles.backButton}
                onClick={() => setActiveSection("dimensions")}
              >
                <i className="fas fa-arrow-left"></i> Back to Dimensions
              </button>
              <button
                style={styles.nextButton}
                onClick={() => setActiveSection("features")}
              >
                Safety Features <i className="fas fa-arrow-right"></i>
              </button>
            </div>
          </motion.div>
        )}

        {/* Features Section */}
        {activeSection === "features" && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
            style={styles.sectionContent}
          >
            {/* Safety Features */}
            <div style={styles.sectionHeader}>
              <i className="fas fa-shield-alt" style={styles.sectionIcon}></i>
              <h3>Safety Features</h3>
            </div>

            <div
              style={{
                ...styles.featuresContainer,
                ...responsiveStyles.featuresContainer,
              }}
            >
              {/* Emergency Power */}
              <div
                style={{
                  ...styles.featureItem,
                  ...(isFeatureEnabled(configData.safety, "emergencyPower")
                    ? styles.featureEnabled
                    : styles.featureDisabled),
                }}
              >
                <div style={styles.featureIconWrap}>
                  <i className="fas fa-plug" style={styles.featureIcon}></i>
                </div>
                <div style={styles.featureInfo}>
                  <div style={styles.featureName}>Emergency Power</div>
                  <div style={styles.featureStatus}>
                    {isFeatureEnabled(configData.safety, "emergencyPower") ? (
                      <>
                        <i className="fas fa-check-circle"></i> Included
                      </>
                    ) : (
                      <>
                        <i className="fas fa-times-circle"></i> Not included
                      </>
                    )}
                  </div>
                </div>
              </div>

              {/* Fire Service */}
              <div
                style={{
                  ...styles.featureItem,
                  ...(isFeatureEnabled(configData.safety, "fireService")
                    ? styles.featureEnabled
                    : styles.featureDisabled),
                }}
              >
                <div style={styles.featureIconWrap}>
                  <i
                    className="fas fa-fire-extinguisher"
                    style={styles.featureIcon}
                  ></i>
                </div>
                <div style={styles.featureInfo}>
                  <div style={styles.featureName}>Fire Operation</div>
                  <div style={styles.featureStatus}>
                    {isFeatureEnabled(configData.safety, "fireAlarm", [
                      "fireService",
                    ]) ? (
                      <>
                        <i className="fas fa-check-circle"></i> Included
                      </>
                    ) : (
                      <>
                        <i className="fas fa-times-circle"></i> Not included
                      </>
                    )}
                  </div>
                </div>
              </div>

              {/* Emergency Intercom */}
              <div
                style={{
                  ...styles.featureItem,
                  ...(isFeatureEnabled(configData.safety, "intercom")
                    ? styles.featureEnabled
                    : styles.featureDisabled),
                }}
              >
                <div style={styles.featureIconWrap}>
                  <i
                    className="fas fa-phone-alt"
                    style={styles.featureIcon}
                  ></i>
                </div>
                <div style={styles.featureInfo}>
                  <div style={styles.featureName}>Emergency Intercom</div>
                  <div style={styles.featureStatus}>
                    {isFeatureEnabled(configData.safety, "emergencyCall", [
                      "intercom",
                    ]) ? (
                      <>
                        <i className="fas fa-check-circle"></i> Included
                      </>
                    ) : (
                      <>
                        <i className="fas fa-times-circle"></i> Not included
                      </>
                    )}
                  </div>
                </div>
              </div>

              {/* Security Camera */}
              <div
                style={{
                  ...styles.featureItem,
                  ...(isFeatureEnabled(configData.safety, "camera")
                    ? styles.featureEnabled
                    : styles.featureDisabled),
                }}
              >
                <div style={styles.featureIconWrap}>
                  <i className="fas fa-video" style={styles.featureIcon}></i>
                </div>
                <div style={styles.featureInfo}>
                  <div style={styles.featureName}>Security Camera</div>
                  <div style={styles.featureStatus}>
                    {isFeatureEnabled(configData.safety, "securityCamera", [
                      "camera",
                    ]) ? (
                      <>
                        <i className="fas fa-check-circle"></i> Included
                      </>
                    ) : (
                      <>
                        <i className="fas fa-times-circle"></i> Not included
                      </>
                    )}
                  </div>
                </div>
              </div>

              {/* Overload Detection */}
              <div
                style={{
                  ...styles.featureItem,
                  ...(isFeatureEnabled(configData.safety, "overloadDetection")
                    ? styles.featureEnabled
                    : styles.featureDisabled),
                }}
              >
                <div style={styles.featureIconWrap}>
                  <i
                    className="fas fa-balance-scale"
                    style={styles.featureIcon}
                  ></i>
                </div>
                <div style={styles.featureInfo}>
                  <div style={styles.featureName}>Overload Detection</div>
                  <div style={styles.featureStatus}>
                    {isFeatureEnabled(
                      configData.safety,
                      "overloadDetection"
                    ) ? (
                      <>
                        <i className="fas fa-check-circle"></i> Included
                      </>
                    ) : (
                      <>
                        <i className="fas fa-times-circle"></i> Not included
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Accessibility Features - New Section with divider */}
            <div style={styles.featureDivider}></div>

            <div style={{ ...styles.sectionHeader, marginTop: "30px" }}>
              <i
                className="fas fa-universal-access"
                style={styles.sectionIcon}
              ></i>
              <h3>Accessibility Features</h3>
            </div>

            <div
              style={{
                ...styles.featuresContainer,
                ...responsiveStyles.featuresContainer,
              }}
            >
              {/* Braille Buttons */}
              <div
                style={{
                  ...styles.featureItem,
                  ...(isFeatureEnabled(
                    configData.accessibility,
                    "brailleButtons"
                  )
                    ? styles.featureEnabled
                    : styles.featureDisabled),
                }}
              >
                <div style={styles.featureIconWrap}>
                  <i className="fas fa-braille" style={styles.featureIcon}></i>
                </div>
                <div style={styles.featureInfo}>
                  <div style={styles.featureName}>Braille Markings</div>
                  <div style={styles.featureStatus}>
                    {isFeatureEnabled(configData.accessibility, "braille", [
                      "brailleButtons",
                    ]) ? (
                      <>
                        <i className="fas fa-check-circle"></i> Included
                      </>
                    ) : (
                      <>
                        <i className="fas fa-times-circle"></i> Not included
                      </>
                    )}
                  </div>
                </div>
              </div>

              {/* Voice Announcements */}
              <div
                style={{
                  ...styles.featureItem,
                  ...(isFeatureEnabled(
                    configData.accessibility,
                    "voiceAnnouncement"
                  )
                    ? styles.featureEnabled
                    : styles.featureDisabled),
                }}
              >
                <div style={styles.featureIconWrap}>
                  <i
                    className="fas fa-volume-up"
                    style={styles.featureIcon}
                  ></i>
                </div>
                <div style={styles.featureInfo}>
                  <div style={styles.featureName}>Voice Announcements</div>
                  <div style={styles.featureStatus}>
                    {isFeatureEnabled(
                      configData.accessibility,
                      "voiceAnnouncements",
                      ["voiceAnnouncement"]
                    ) ? (
                      <>
                        <i className="fas fa-check-circle"></i> Included
                      </>
                    ) : (
                      <>
                        <i className="fas fa-times-circle"></i> Not included
                      </>
                    )}
                  </div>
                </div>
              </div>

              {/* Visibility Mirror */}
              <div
                style={{
                  ...styles.featureItem,
                  ...(isFeatureEnabled(configData.accessibility, "mirror")
                    ? styles.featureEnabled
                    : styles.featureDisabled),
                }}
              >
                <div style={styles.featureIconWrap}>
                  <i className="fas fa-eye" style={styles.featureIcon}></i>
                </div>
                <div style={styles.featureInfo}>
                  <div style={styles.featureName}>Visibility Mirror</div>
                  <div style={styles.featureStatus}>
                    {isFeatureEnabled(
                      configData.accessibility,
                      "visibilityMirror",
                      ["mirror"]
                    ) ? (
                      <>
                        <i className="fas fa-check-circle"></i> Included
                      </>
                    ) : (
                      <>
                        <i className="fas fa-times-circle"></i> Not included
                      </>
                    )}
                  </div>
                </div>
              </div>

              {/* Lowered Buttons */}
              <div
                style={{
                  ...styles.featureItem,
                  ...(isFeatureEnabled(
                    configData.accessibility,
                    "loweredButtons"
                  )
                    ? styles.featureEnabled
                    : styles.featureDisabled),
                }}
              >
                <div style={styles.featureIconWrap}>
                  <i
                    className="fas fa-arrow-down"
                    style={styles.featureIcon}
                  ></i>
                </div>
                <div style={styles.featureInfo}>
                  <div style={styles.featureName}>Lowered Buttons</div>
                  <div style={styles.featureStatus}>
                    {isFeatureEnabled(
                      configData.accessibility,
                      "loweredButtons"
                    ) ? (
                      <>
                        <i className="fas fa-check-circle"></i> Included
                      </>
                    ) : (
                      <>
                        <i className="fas fa-times-circle"></i> Not included
                      </>
                    )}
                  </div>
                </div>
              </div>

              {/* Handrails */}
              <div
                style={{
                  ...styles.featureItem,
                  ...(isFeatureEnabled(configData.accessibility, "handrails")
                    ? styles.featureEnabled
                    : styles.featureDisabled),
                }}
              >
                <div style={styles.featureIconWrap}>
                  <i
                    className="fas fa-grip-lines"
                    style={styles.featureIcon}
                  ></i>
                </div>
                <div style={styles.featureInfo}>
                  <div style={styles.featureName}>Handrails</div>
                  <div style={styles.featureStatus}>
                    {isFeatureEnabled(configData.accessibility, "handrails") ? (
                      <>
                        <i className="fas fa-check-circle"></i> Included
                      </>
                    ) : (
                      <>
                        <i className="fas fa-times-circle"></i> Not included
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div
              style={{
                ...styles.navigationButtons,
                ...responsiveStyles.navigationButtons,
              }}
            >
              <button
                style={styles.backButton}
                onClick={() => setActiveSection("design")}
              >
                <i className="fas fa-arrow-left"></i> Back to Design
              </button>
              <button
                style={styles.nextButton}
                onClick={() => setActiveSection("contact")}
              >
                Contact Form <i className="fas fa-arrow-right"></i>
              </button>
            </div>
          </motion.div>
        )}

        {/* Contact Form Section */}
        {activeSection === "contact" && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
            style={styles.sectionContent}
          >
            <div style={styles.sectionHeader}>
              <i className="fas fa-envelope" style={styles.sectionIcon}></i>
              <h3>Contact Information</h3>
            </div>

            {submitStatus === "success" ? (
              <div
                style={{
                  ...styles.successMessage,
                  ...responsiveStyles.successMessage,
                }}
              >
                <div style={styles.successIconContainer}>
                  <i
                    className="fas fa-check-circle"
                    style={styles.successIcon}
                  ></i>
                </div>
                <h3 style={styles.successTitle}>
                  Configuration Submitted Successfully!
                </h3>
                <p style={styles.successText}>
                  Thank you for your inquiry. A confirmation email will be sent
                  to {customerInfo.email} shortly. Our team will contact you
                  within 48 hours with your personalized quote.
                </p>

                {showDownloadOptions && (
                  <div
                    style={{
                      ...styles.downloadSection,
                      ...responsiveStyles.downloadSection,
                    }}
                  >
                    <a
                      href={pdfDownloadUrl}
                      download={`elevator-quote-${quoteId}.pdf`}
                      style={{
                        ...styles.downloadLink,
                        ...responsiveStyles.downloadLink,
                      }}
                    >
                      <i className="fas fa-file-pdf"></i>
                      Download Configuration PDF
                    </a>

                    <button
                      onClick={onFinish}
                      style={{
                        ...styles.finishButton,
                        ...responsiveStyles.finishButton,
                      }}
                    >
                      Finish <i className="fas fa-check"></i>
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <form onSubmit={handleSubmit}>
                <p style={styles.formDescription}>
                  Please provide your details so we can prepare your custom
                  quote and contact you regarding your elevator configuration.
                </p>

                <div
                  style={{ ...styles.formGrid, ...responsiveStyles.formGrid }}
                >
                  <div style={styles.formGroup}>
                    <label htmlFor="name" style={styles.label}>
                      Full Name <span style={styles.required}>*</span>
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={customerInfo.name}
                      onChange={handleInputChange}
                      style={styles.input}
                      required
                    />
                  </div>

                  <div style={styles.formGroup}>
                    <label htmlFor="email" style={styles.label}>
                      Email Address <span style={styles.required}>*</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={customerInfo.email}
                      onChange={handleInputChange}
                      style={styles.input}
                      required
                    />
                  </div>

                  <div style={styles.formGroup}>
                    <label htmlFor="phone" style={styles.label}>
                      Phone Number <span style={styles.required}>*</span>
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={customerInfo.phone}
                      onChange={handleInputChange}
                      style={styles.input}
                      required
                    />
                  </div>

                  <div style={styles.formGroup}>
                    <label htmlFor="company" style={styles.label}>
                      Company/Organization
                    </label>
                    <input
                      type="text"
                      id="company"
                      name="company"
                      value={customerInfo.company}
                      onChange={handleInputChange}
                      style={styles.input}
                    />
                  </div>

                  <div style={styles.formGroup}>
                    <label htmlFor="projectName" style={styles.label}>
                      Project Name
                    </label>
                    <input
                      type="text"
                      id="projectName"
                      name="projectName"
                      value={customerInfo.projectName}
                      onChange={handleInputChange}
                      style={styles.input}
                    />
                  </div>

                  <div style={styles.formGroup}>
                    <label htmlFor="installationAddress" style={styles.label}>
                      Installation Address
                    </label>
                    <input
                      type="text"
                      id="installationAddress"
                      name="installationAddress"
                      value={customerInfo.installationAddress}
                      onChange={handleInputChange}
                      style={styles.input}
                      required
                    />
                  </div>

                  <div style={{ ...styles.formGroup, gridColumn: "1 / -1" }}>
                    <label htmlFor="comments" style={styles.label}>
                      Additional Comments
                    </label>
                    <textarea
                      id="comments"
                      name="comments"
                      value={customerInfo.comments}
                      onChange={handleInputChange}
                      style={{ ...styles.input, ...styles.textarea }}
                      rows={4}
                    />
                  </div>
                </div>

                {errorMessage && (
                  <div style={styles.errorMessage}>{errorMessage}</div>
                )}

                <div
                  style={{
                    ...styles.formButtons,
                    ...responsiveStyles.formButtons,
                  }}
                >
                  <button
                    type="button"
                    style={styles.backButton}
                    onClick={() => setActiveSection("features")}
                  >
                    <i className="fas fa-arrow-left"></i> Back to Features
                  </button>
                  <button
                    type="submit"
                    style={styles.submitButton}
                    disabled={submitStatus === "submitting"}
                  >
                    {submitStatus === "submitting" ? (
                      <>
                        <i className="fas fa-spinner fa-spin"></i> Submitting...
                      </>
                    ) : (
                      <>
                        Submit Configuration{" "}
                        <i className="fas fa-paper-plane"></i>
                      </>
                    )}
                  </button>
                </div>
              </form>
            )}
          </motion.div>
        )}
      </div>
    </div>
  );
};

const styles = {
  container: {
    fontFamily: "'Segoe UI', 'Roboto', 'Helvetica Neue', sans-serif",
  },
  tabsContainer: {
    display: "flex",
    justifyContent: "center",
    gap: "10px",
    marginBottom: "30px",
    flexWrap: "wrap",
  },
  tab: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "6px",
    padding: "12px 18px",
    backgroundColor: "#f5f5f7",
    border: "none",
    borderRadius: "12px",
    cursor: "pointer",
    fontSize: "13px",
    fontWeight: "500",
    color: "#555",
    minWidth: "90px",
  },
  activeTab: {
    backgroundColor: "#163ba2",
    color: "white",
    boxShadow: "0 4px 12px rgba(22, 59, 162, 0.2)",
  },
  contentContainer: {
    backgroundColor: "#f9f9fb",
    padding: "30px",
    borderRadius: "16px",
    boxShadow: "0 4px 20px rgba(0,0,0,0.06)",
  },
  quoteHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: "30px",
  },
  quoteTitle: {
    fontSize: "24px",
    fontWeight: "600",
    color: "#1d1d1f",
    margin: "0 0 8px 0",
  },
  quoteId: {
    fontSize: "14px",
    color: "#163ba2",
    fontWeight: "500",
    margin: 0,
    padding: "6px 12px",
    borderRadius: "6px",
    backgroundColor: "#e6efff",
    display: "inline-block",
  },
  dateBox: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    padding: "8px 15px",
    backgroundColor: "white",
    borderRadius: "8px",
    fontSize: "14px",
    fontWeight: "500",
    boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
  },
  dateIcon: {
    color: "#163ba2",
  },

  // Overview Section
  overviewContainer: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: "25px",
    marginBottom: "40px",
  },
  overviewCard: {
    backgroundColor: "white",
    padding: "25px",
    borderRadius: "16px",
    boxShadow: "0 4px 15px rgba(0,0,0,0.05)",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    textAlign: "center",
  },
  overviewIconContainer: {
    width: "60px",
    height: "60px",
    backgroundColor: "#e6efff",
    borderRadius: "50%",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    marginBottom: "15px",
  },
  overviewIcon: {
    fontSize: "24px",
    color: "#163ba2",
  },
  overviewTitle: {
    fontSize: "16px",
    fontWeight: "600",
    color: "#4d4d4d",
    margin: "0 0 5px 0",
  },
  overviewValue: {
    fontSize: "18px",
    fontWeight: "700",
    color: "#1d1d1f",
    margin: "0 0 5px 0",
  },
  overviewDetail: {
    fontSize: "14px",
    color: "#666",
    margin: "0 0 15px 0",
  },
  overviewButton: {
    backgroundColor: "transparent",
    border: "1px solid #e0e0e0",
    borderRadius: "8px",
    padding: "8px 15px",
    fontSize: "13px",
    fontWeight: "500",
    color: "#163ba2",
    cursor: "pointer",
    marginTop: "auto",
  },
  submitContainer: {
    textAlign: "center",
    marginTop: "20px",
    padding: "25px",
    backgroundColor: "white",
    borderRadius: "16px",
    boxShadow: "0 4px 15px rgba(0,0,0,0.05)",
  },
  submitText: {
    fontSize: "16px",
    color: "#4d4d4d",
    marginBottom: "20px",
  },
  submitButton: {
    backgroundColor: "#163ba2",
    color: "white",
    border: "none",
    borderRadius: "10px",
    padding: "14px 28px",
    fontSize: "16px",
    fontWeight: "500",
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    gap: "10px",
    boxShadow: "0 4px 12px rgba(22, 59, 162, 0.2)",
  },
  buttonIcon: {
    marginLeft: "5px",
  },

  // Section Content
  sectionContent: {
    display: "flex",
    flexDirection: "column",
    gap: "25px",
  },
  sectionHeader: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    marginBottom: "10px",
  },
  sectionIcon: {
    fontSize: "22px",
    color: "#163ba2",
  },

  // Dimensions Section
  dimensionsContainer: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
    gap: "20px",
    marginBottom: "20px",
  },
  dimensionCard: {
    backgroundColor: "white",
    padding: "20px",
    borderRadius: "12px",
    boxShadow: "0 2px 10px rgba(0,0,0,0.04)",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    textAlign: "center",
  },
  dimensionIconWrap: {
    width: "50px",
    height: "50px",
    backgroundColor: "#e6efff",
    borderRadius: "10px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    marginBottom: "15px",
  },
  dimensionIcon: {
    fontSize: "20px",
    color: "#163ba2",
  },
  dimensionLabel: {
    fontSize: "14px",
    color: "#666",
    marginBottom: "5px",
  },
  dimensionValue: {
    fontSize: "20px",
    fontWeight: "600",
    color: "#1d1d1f",
  },

  // Design Section
  designGrid: {
    display: "flex",
    flexDirection: "column",
    gap: "30px",
    marginBottom: "20px",
  },
  designCategory: {
    backgroundColor: "white",
    padding: "25px",
    borderRadius: "12px",
    boxShadow: "0 2px 10px rgba(0,0,0,0.04)",
  },
  designCategoryTitle: {
    fontSize: "16px",
    fontWeight: "600",
    color: "#1d1d1f",
    marginTop: 0,
    marginBottom: "20px",
  },
  designItemsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
    gap: "20px",
  },
  designItem: {
    backgroundColor: "#f5f5f7",
    padding: "15px",
    borderRadius: "10px",
  },
  designItemLabel: {
    fontSize: "13px",
    color: "#666",
    marginBottom: "6px",
  },
  designItemValue: {
    fontSize: "16px",
    fontWeight: "600",
    color: "#1d1d1f",
  },

  // Features Section
  featuresContainer: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
    gap: "20px",
    marginBottom: "20px",
  },
  featureItem: {
    display: "flex",
    alignItems: "center",
    gap: "20px",
    padding: "20px",
    borderRadius: "12px",
    boxShadow: "0 2px 10px rgba(0,0,0,0.04)",
  },
  featureEnabled: {
    backgroundColor: "#e8f5ea",
    borderLeft: "4px solid #2d862d",
  },
  featureDisabled: {
    backgroundColor: "#f5f5f7",
    opacity: 0.8,
  },
  featureIconWrap: {
    width: "46px",
    height: "46px",
    borderRadius: "10px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  featureIcon: {
    fontSize: "20px",
    color: "#333",
  },
  featureInfo: {
    flex: 1,
  },
  featureName: {
    fontSize: "16px",
    fontWeight: "600",
    color: "#1d1d1f",
    marginBottom: "5px",
  },
  featureStatus: {
    fontSize: "14px",
    display: "flex",
    alignItems: "center",
    gap: "6px",
  },

  // Navigation Buttons
  navigationButtons: {
    display: "flex",
    justifyContent: "space-between",
    marginTop: "10px",
  },
  backButton: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    backgroundColor: "transparent",
    border: "1px solid #ddd",
    padding: "12px 20px",
    borderRadius: "8px",
    fontSize: "15px",
    fontWeight: "500",
    color: "#4d4d4d",
    cursor: "pointer",
  },
  nextButton: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    backgroundColor: "#163ba2",
    color: "white",
    border: "none",
    padding: "12px 20px",
    borderRadius: "8px",
    fontSize: "15px",
    fontWeight: "500",
    cursor: "pointer",
    boxShadow: "0 4px 12px rgba(22, 59, 162, 0.2)",
  },

  // Form Styles
  formDescription: {
    fontSize: "16px",
    color: "#4d4d4d",
    marginBottom: "30px",
    lineHeight: "1.5",
  },
  formGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, 1fr)",
    gap: "20px",
    marginBottom: "30px",
  },
  formGroup: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },
  label: {
    fontSize: "14px",
    fontWeight: "500",
    color: "#4d4d4d",
  },
  required: {
    color: "#cf3636",
  },
  input: {
    padding: "12px 15px",
    borderRadius: "8px",
    border: "1px solid #ddd",
    fontSize: "15px",
  },
  textarea: {
    resize: "vertical",
    minHeight: "120px",
  },
  errorMessage: {
    backgroundColor: "#ffebee",
    color: "#cf3636",
    padding: "12px 15px",
    borderRadius: "8px",
    fontSize: "14px",
    marginBottom: "20px",
    borderLeft: "4px solid #cf3636",
  },
  formButtons: {
    display: "flex",
    justifyContent: "space-between",
    marginTop: "20px",
  },
  submitButton: {
    backgroundColor: "#163ba2",
    color: "white",
    border: "none",
    borderRadius: "8px",
    padding: "14px 24px",
    fontSize: "15px",
    fontWeight: "500",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: "10px",
    boxShadow: "0 4px 12px rgba(22, 59, 162, 0.2)",
  },

  // Success Message
  successMessage: {
    textAlign: "center",
    padding: "30px 20px",
  },
  successIconContainer: {
    width: "80px",
    height: "80px",
    backgroundColor: "#e8f5ea",
    borderRadius: "50%",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    margin: "0 auto 20px",
  },
  successIcon: {
    fontSize: "40px",
    color: "#2d862d",
  },
  successTitle: {
    fontSize: "24px",
    fontWeight: "600",
    color: "#2d862d",
    margin: "0 0 15px 0",
  },
  successText: {
    fontSize: "16px",
    color: "#4d4d4d",
    lineHeight: "1.5",
    marginBottom: "30px",
  },
  downloadSection: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "20px",
  },
  downloadLink: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    backgroundColor: "#f0f7ff",
    color: "#163ba2",
    textDecoration: "none",
    padding: "12px 20px",
    borderRadius: "8px",
    fontWeight: "500",
  },
  finishButton: {
    backgroundColor: "#2d862d",
    color: "white",
    border: "none",
    borderRadius: "8px",
    padding: "14px 30px",
    fontSize: "16px",
    fontWeight: "500",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: "10px",
    boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
  },

  progressContainer: {
    marginBottom: "25px",
  },
  progressLabel: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "8px",
  },
  progressStep: {
    fontSize: "14px",
    color: "#666",
    fontWeight: "500",
  },
  progressTitle: {
    fontSize: "16px",
    color: "#163ba2",
    fontWeight: "600",
  },
  progressBarContainer: {
    height: "6px",
    backgroundColor: "#e6efff",
    borderRadius: "3px",
    overflow: "hidden",
  },
  progressBar: {
    height: "100%",
    backgroundColor: "#163ba2",
    borderRadius: "3px",
  },
  tabsContainer: {
    display: "flex",
    justifyContent: "center",
    gap: "10px",
    marginBottom: "30px",
    flexWrap: "wrap",
    position: "relative",
  },
  tab: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "6px",
    padding: "16px 22px",
    backgroundColor: "#f5f5f7",
    border: "none",
    borderRadius: "12px",
    cursor: "pointer",
    fontSize: "13px",
    fontWeight: "500",
    color: "#555",
    minWidth: "100px",
    position: "relative",
    transition: "background-color 0.3s, transform 0.3s, box-shadow 0.3s",
  },
  activeTab: {
    backgroundColor: "#e6efff",
    color: "#163ba2",
    boxShadow: "0 4px 12px rgba(22, 59, 162, 0.1)",
  },
  tabIconContainer: {
    width: "44px",
    height: "44px",
    backgroundColor: "white",
    borderRadius: "50%",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    marginBottom: "8px",
    boxShadow: "0 2px 6px rgba(0,0,0,0.05)",
    transition: "background-color 0.3s",
  },
  tabIcon: {
    fontSize: "18px",
    color: "#555",
    transition: "color 0.3s",
  },
  tabText: {
    fontSize: "14px",
    fontWeight: "500",
    transition: "color 0.3s",
  },
  tabIndicator: {
    position: "absolute",
    bottom: "-8px",
    width: "40px",
    height: "4px",
    backgroundColor: "#163ba2",
    borderRadius: "2px",
    transition: "width 0.3s ease",
  },
  specSectionDivider: {
    marginTop: "25px",
    marginBottom: "15px",
    borderBottom: "1px solid #e6efff",
    paddingBottom: "8px",
  },
  specSectionTitle: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    fontSize: "16px",
    fontWeight: "600",
    color: "#163ba2",
  },
  specSectionIcon: {
    fontSize: "16px",
    color: "#163ba2",
  },
};

export default ConfigSummary;
